import { PubObject } from "./FindPubs";
import { calculateBeerMugRating, categorizeGardenSize } from "../utils/ratingCalculations";
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Rating,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    useTheme,
    useMediaQuery,
    IconButton,
    Stack,
    Tooltip,
} from "@mui/material";
import { Helmet } from 'react-helmet';
import IntensityTimeline from "./IntensityTimeline";
import { getPubRaycasting, getNearbyPubs, getPubWithNearby, getFullMeshes, invalidatePubRaycastingCache } from "../API";
import React, { useState, useEffect, useMemo, lazy, Suspense, useCallback, useRef } from "react";
import { themeColors } from "../styles/theme";
import Fonts from './Fonts';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShareModal from './ShareModal';
import ShareIcon from '@mui/icons-material/Share';
import {
    trackPubView,
    trackNoGardenBounce,
    trackGardenViewDuration,
    trackGardenEditStarted,
    trackShareInitiated,
    trackReturnToSearch,
    track3DSceneLoadingTime
} from "../utils/analytics";
import NearbyPubs from './NearbyPubs';
import { extractCity } from '../utils/cityExtractor';
import { SunIcon } from "./SunIcon";
import { Footer } from './Footer';
import { BottomSheet, BottomSheetRef } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import { MouseIcon } from './Icons';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import SubmitGardenMapbox from './garden_edit/SubmitGardenMapbox';
import type { FullMeshResponse, ExistingGarden, SubmitGardenRef } from './SubmitGarden';
import VerifiedBadge from './VerifiedBadge';
import { isCrawler } from '../utils/isCrawler';

const ShadedVertexViewer = lazy(() => import('./ViewSunlight3D'));
const SubmitGarden = lazy(() => import('./SubmitGarden'));

interface HeatmapData { timestamps: string[]; values: number[]; }
interface IntensityData { timestamps: string[]; values: { [googlePlaceId: string]: number[]; }; }
interface Garden { name: string; uid: string; coords: Array<[number, number]>; raycastResults: { [timestamp: string]: number[]; }; averageSun: number[]; }
interface PubRaycastingResponse {
    pub_id: string;
    date: string;
    original_query_date: string;
    status?: string;
    message?: string;
    garden_names?: Record<string, string>;
    garden_details?: {
        [garden_uid: string]: {
            name: string;
            is_uncertain: boolean;
            is_owner?: boolean;
            garden_size?: number;
        }
    };
    has_no_garden?: boolean;
    garden_moderated?: boolean;
    raycast_results?: any;
    garden_coords?: any;
    sun_times?: { sunrise: string; sunset: string; };
    above_horizon_steps?: number;
    average_sun?: number[];
    heatmap_1d?: HeatmapData;
    mesh_uris?: string[];
    centre_ecef?: [number, number, number];
    session_id?: string;
    copyrights?: string[];
    sun_vector?: { [timestamp: string]: [number, number, number] };
    garden_polygons?: { [garden_uid: string]: { polygon: [number, number][]; elevation: number | null; } };
    coverage_quality?: 'HIGH' | 'MEDIUM' | 'LOW' | null;  // Solar API data quality level
    [gardenId: string]: any;
    city?: string;
    latitude?: number;
    longitude?: number;
}
interface PubYearlySunPoints { status: 'success' | 'no_data' | 'error'; dates: string[]; sun_percentages: number[]; message?: string; }

const latLonElevToECEF = (lat: number, lon: number, elevation: number): [number, number, number] => {
    const latRad = (lat * Math.PI) / 180;
    const lonRad = (lon * Math.PI) / 180;
    const a = 6378137.0;
    const f = 1/298.257223563;
    const e2 = 2*f - f*f;
    const N = a / Math.sqrt(1 - e2 * Math.sin(latRad) * Math.sin(latRad));
    const X = (N + elevation) * Math.cos(latRad) * Math.cos(lonRad);
    const Y = (N + elevation) * Math.cos(latRad) * Math.sin(lonRad);
    const Z = (N * (1 - e2) + elevation) * Math.sin(latRad);
    return [X, Y, Z];
};

const convertToIntensityData = (data: HeatmapData | undefined, id: string): IntensityData => {
    if (!data || !data.timestamps || !data.values) {
        return { timestamps: [], values: { [id]: [] } };
    }
    return { timestamps: data.timestamps, values: { [id]: data.values } };
};

const SubmitGardenButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '12px',
    padding: '10px 20px',
    background: '#4CAF50',
    color: '#fff',
    boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)',
    '&:hover': {
        background: '#45a049',
        boxShadow: '0 6px 14px rgba(76, 175, 80, 0.4)',
    },
}));

interface ViewPubProps {
    pubToView?: PubObject;
    googlePlaceId?: string;
}

const PEEK_HEIGHT_NUM = 220;
const PEEK_HEIGHT_EXPANDED = 110;
const MAX_SHEET_HEIGHT_VH = 85;
const SHEET_BORDER_RADIUS = '16px';
const MAX_SHEET_WIDTH = '700px';

const HeaderButtonStyle = {
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '48px',
    padding: '6px 10px',
    fontSize: '0.9rem',
    background: 'transparent',
    color: '#000',
    border: `1.5px solid black`,
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.05)',
        borderColor: 'black',
    },
    minWidth: '80px',
    flexShrink: 0,
    flexGrow: 1,
    textAlign: 'center',
    mr: 0.5
};

const ViewPub = ({ pubToView: propPubToView, googlePlaceId: propGooglePlaceId }: ViewPubProps) => {
    const { calculateDistance } = useLocationContext();
    
    const navigate = useNavigate();
    const location = useLocation();
    const { googlePlaceId: urlPubId } = useParams<{ googlePlaceId: string }>();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery('(max-width:600px)');
    const timelineMode = 'compact';

    const effectiveGooglePlaceId = urlPubId || propGooglePlaceId;

    // Memoize the result so it's only calculated once per render
    const isBot = useMemo(() => {
        // Debug parameter to force bot mode
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('debug') === 'bot') {
            return true;
        }
        return isCrawler();
    }, []);

    const [pubData, setPubData] = useState<PubObject | null>(null);
    const [raycastingData, setRaycastingData] = useState<PubRaycastingResponse | null>(null);
    const [isLoadingPub, setIsLoadingPub] = useState<boolean>(true);
    const [isLoadingRaycast, setIsLoadingRaycast] = useState<boolean>(false);
    const [isLoadingYearly, setIsLoadingYearly] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isEditingGarden, setIsEditingGarden] = useState<boolean>(false);
    const [sceneLoadStartTime, setSceneLoadStartTime] = useState<number | null>(null);
    const [nearbyPubsData, setNearbyPubsData] = useState<any[]>([]);
    const [isLoadingNearby, setIsLoadingNearby] = useState<boolean>(false);
    const [attributionOpen, setAttributionOpen] = useState<boolean>(false);
    const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
    const [tileCopyrights, setTileCopyrights] = useState<string[]>([]);
    const [hasUserLocation, setHasUserLocation] = useState<boolean>(false);
    const [userDistance, setUserDistance] = useState<number | undefined>(undefined);
    const [yearlySunData, setYearlySunData] = useState<any[]>([]);
    const [isSheetExpanded, setIsSheetExpanded] = useState<boolean>(false);
    const [isProcessingSubmission, setIsProcessingSubmission] = useState<boolean>(false);

    const pageLoadTimeRef = useRef<number>(Date.now());
    const timeOnPageRef = useRef<number>(0);
    const raycastingFetchedRef = useRef<boolean>(false);
    const yearlyDataFetchedRef = useRef<boolean>(false);
    const sheetRef = useRef<BottomSheetRef>(null);

    const handleExitAndRefresh = useCallback(async () => {
        setIsEditingGarden(false);
        setIsProcessingSubmission(true);
        raycastingFetchedRef.current = false; // Force refetch
        
        // Clear the client-side cache for this pub
        invalidatePubRaycastingCache(effectiveGooglePlaceId!);
        
        try {
            // This is the explicit refresh logic, now tied to user action
            const pubResponse = await getPubWithNearby(effectiveGooglePlaceId!);
            if (pubResponse.data?.pub) {
                const pubDetails = pubResponse.data.pub as PubObject;
                if (!pubDetails.city && pubDetails.address) {
                    pubDetails.city = extractCity(pubDetails.address) || undefined;
                }
                setPubData(pubDetails);
                if (pubDetails.garden_drawn) {
                    const raycastResponse = await getPubRaycasting(effectiveGooglePlaceId!, true, true); // skip cache
                    setRaycastingData(raycastResponse.data);
                }
            }
        } catch (error) {
            console.error("Error refreshing data after submission:", error);
            setError("Failed to load updated garden data. Please refresh the page.");
        } finally {
            setIsProcessingSubmission(false);
        }
    }, [effectiveGooglePlaceId]);

    const handleCancelEditing = useCallback(() => {
        setIsEditingGarden(false);
    }, []);

    useEffect(() => {
        setError(null);
        setIsLoadingPub(true);
        setIsLoadingRaycast(false);
        setIsLoadingYearly(false);
        setRaycastingData(null);
        setYearlySunData([]);
        setNearbyPubsData([]);
        setIsLoadingNearby(false);
        setPubData(null);
        raycastingFetchedRef.current = false;
        yearlyDataFetchedRef.current = false;
        setIsSheetExpanded(false);
        sheetRef.current?.snapTo(({ snapPoints }) => snapPoints[0]);
        pageLoadTimeRef.current = Date.now();

        const locationStatePub = location.state?.pubToView as PubObject | undefined;
        const locationStateNearbyPubs = location.state?.nearbyPubs as PubObject[] | undefined;
        const pubIdToLoad = effectiveGooglePlaceId;

        const fetchNearbyIfNeeded = (lat?: number, lon?: number) => {
            if (lat && lon && nearbyPubsData.length === 0) {
                setIsLoadingNearby(true);
                getNearbyPubs(lat, lon, 500, undefined, false, 5)  // ORM lookup is false to make this significantly cheaper (since we don't have to fluff it up with extra data). Only return 5 pubs.
                    .then(response => {
                        if (response.data.pubs?.length > 0) {
                            setNearbyPubsData(response.data.pubs);
                        }
                    })
                    .catch(err => console.error("Error fetching nearby pubs separately:", err))
                    .finally(() => setIsLoadingNearby(false));
            }
        };

        // --- OPTIMIZED LOGIC: Check for pre-loaded data first ---
        
        // CASE 1: Navigated from FindPubs with all the data (OPTIMIZED PATH)
        if (locationStatePub && locationStateNearbyPubs && locationStatePub.google_place_id === pubIdToLoad) {
            // Set main pub data
            if (!locationStatePub.city && locationStatePub.address) {
                locationStatePub.city = extractCity(locationStatePub.address) || undefined;
            }
            setPubData(locationStatePub);
            
            // Set nearby pubs data (already filtered to exclude the current pub)
            setNearbyPubsData(locationStateNearbyPubs);
            setIsLoadingNearby(false);
            
            setIsLoadingPub(false);
            trackPubView(
                locationStatePub.google_place_id,
                locationStatePub.name,
                !!(locationStatePub.garden_drawn && !locationStatePub.has_no_garden),
                !!locationStatePub.has_no_garden
            );
        } 
        // CASE 2: Navigated from FindPubs but without nearby pubs data (fallback)
        else if (locationStatePub && locationStatePub.google_place_id === pubIdToLoad) {
            if (!locationStatePub.city && locationStatePub.address) {
                locationStatePub.city = extractCity(locationStatePub.address) || undefined;
            }
            setPubData(locationStatePub);
            fetchNearbyIfNeeded(locationStatePub.latitude, locationStatePub.longitude);
            setIsLoadingPub(false);
            trackPubView(
                locationStatePub.google_place_id,
                locationStatePub.name,
                !!(locationStatePub.garden_drawn && !locationStatePub.has_no_garden),
                !!locationStatePub.has_no_garden
            );
        } 
        // CASE 3: Direct navigation (e.g., bookmark, external link) - fetch from API
        else if (pubIdToLoad) {
            getPubWithNearby(pubIdToLoad)
                .then(response => {
                    if (response.data?.pub) {
                        const pubDetails = response.data.pub as PubObject;
                        if (!pubDetails.city && pubDetails.address) {
                            pubDetails.city = extractCity(pubDetails.address) || undefined;
                        }
                        setPubData(pubDetails);
                        if (response.data.nearby_pubs?.length > 0) {
                            setNearbyPubsData(response.data.nearby_pubs);
                            setIsLoadingNearby(false);
                        } else {
                            fetchNearbyIfNeeded(pubDetails.latitude, pubDetails.longitude);
                        }
                        // Fetch mesh data to check has_no_garden status
                        getFullMeshes(pubDetails.google_place_id, pubDetails.latitude, pubDetails.longitude)
                            .then(meshResponse => {
                                if (meshResponse.data?.has_no_garden) {
                                    setPubData(prev => prev ? { ...prev, has_no_garden: true } : null);
                                }
                            })
                            .catch(() => {}); // Ignore errors, this is just for the flag
                         trackPubView(
                             pubDetails.google_place_id,
                             pubDetails.name,
                             !!(pubDetails.garden_drawn && !pubDetails.has_no_garden),
                             !!pubDetails.has_no_garden
                         );
                    } else {
                        setError("We couldn't find this pub. It may have been removed or the link is incorrect.");
                    }
                })
                .catch(error => {
                    console.error("Error fetching pub with nearby:", error);
                    if (error.response && error.response.status === 404) {
                        setError("We couldn't find this pub. It may have been removed or the link is incorrect.");
                    } else {
                        setError("Error loading pub data. Please try again.");
                    }
                })
                .finally(() => {
                    setIsLoadingPub(false);
                });
        } else {
            setError("No pub specified. Please return to search.");
            setIsLoadingPub(false);
        }
    // Update dependency array to include location.state for optimization
    }, [location.pathname, location.state, effectiveGooglePlaceId]);

    useEffect(() => {
        const locationPermission = localStorage.getItem('locationPermissionGranted');
        const storedLat = localStorage.getItem('userLatitude');
        const storedLon = localStorage.getItem('userLongitude');
        const hasValidLocation = Boolean(locationPermission === 'true' && storedLat && storedLon);
        setHasUserLocation(hasValidLocation);

        if (pubData?.latitude && pubData?.longitude) {
            const distance = calculateDistance({
                latitude: pubData.latitude,
                longitude: pubData.longitude
            });
            setUserDistance(distance ?? undefined);
        } else {
            setUserDistance(undefined);
        }
    }, [pubData?.latitude, pubData?.longitude, calculateDistance]);

    useEffect(() => {
        if (sheetRef.current) {
            sheetRef.current.snapTo(({ snapPoints }) =>
                isSheetExpanded ? Math.max(...snapPoints) : Math.min(...snapPoints)
            );
        }
    }, [isSheetExpanded]);

    useEffect(() => {
        if (isLoadingPub || !pubData?.google_place_id || (pubData.garden_drawn === false && pubData.has_no_garden !== false) || isProcessingSubmission) {
            return;
        }
        
        if (raycastingFetchedRef.current || isLoadingRaycast) {
            return;
        }

        const fetchRaycastingData = async () => {
            setIsLoadingRaycast(true);
            raycastingFetchedRef.current = true;
            try {
                const response = await getPubRaycasting(pubData.google_place_id, true);
                const data = response.data as PubRaycastingResponse;

                if (data.status === 'error') {
                     throw new Error(data.message || 'Raycasting failed');
                }
                 const updates: Partial<PubObject> = {};
                 if (!pubData.city && data.city) updates.city = data.city;
                 const definitelyNoGarden = data.status === 'no_garden' || data.has_no_garden === true;
                 if (definitelyNoGarden && !pubData.has_no_garden) updates.has_no_garden = true;
                 if (!definitelyNoGarden && data.garden_details && Object.keys(data.garden_details).length > 0 && !pubData.garden_drawn) {
                     updates.garden_drawn = true;
                     updates.has_no_garden = false;
                 } else if (definitelyNoGarden && pubData.garden_drawn) {
                      updates.garden_drawn = false;
                 }

                 if (Object.keys(updates).length > 0) {
                     setPubData(prev => prev ? { ...prev, ...updates } : null);
                 }
                setRaycastingData(data);
                setError(null);
            } catch (err: any) {
                console.error("Error fetching/processing raycasting data:", err);
                
                // Check if this is a 3D coverage error (HTTP 422) - don't retry these
                const is3DCoverageError = err.response?.status === 422 || 
                                         err.response?.data?.error?.includes('3D coverage') ||
                                         err.response?.data?.error?.includes('garden vertices') ||
                                         err.message?.includes('3D coverage');
                
                if (is3DCoverageError) {
                    setError('This area does not have 3D map coverage. Sun data is not available.');
                    // Don't reset raycastingFetchedRef.current to prevent infinite retries
                } else {
                    setError(prevError => prevError || `Sun data unavailable. ${err.message || 'Please try again later.'}`);
                    raycastingFetchedRef.current = false; // Allow retry for other errors
                }
                
                setRaycastingData(null);
            } finally {
                setIsLoadingRaycast(false);
            }
        };
        fetchRaycastingData();
    }, [pubData, isLoadingPub, isLoadingRaycast, isProcessingSubmission]);

    const gardensData = useMemo<Garden[]>(() => {
        if (!raycastingData) return [];
        try {
            const validGardenIds = raycastingData.garden_details ? Object.keys(raycastingData.garden_details) : [];
            const processedGardens = validGardenIds.map(gardenId => {
                const gardenObject = raycastingData[gardenId];
                 if (!gardenObject) return null;
                try {
                    const gardenName = raycastingData.garden_details?.[gardenId]?.name || `Garden ${gardenId.slice(0, 8)}`;
                     const coords3D = gardenObject.garden_vertex_coords || raycastingData.garden_coords?.[gardenId];
                    if (!coords3D || !Array.isArray(coords3D) || coords3D.length === 0) return null;
                    const coords2D = coords3D.map((coord: number[]) => {
                        if (Array.isArray(coord) && coord.length >= 2) return [coord[0], coord[1]] as [number, number];
                        return null;
                    }).filter((coord): coord is [number, number] => coord !== null);
                    if (coords2D.length === 0) return null;
                    let raycastResults = gardenObject.hourly_data || {};
                    let averageSun: number[] = gardenObject.heatmap_1d?.values || [];
                    return { name: gardenName, uid: gardenId, coords: coords2D, raycastResults, averageSun } as Garden;
                } catch (parseErr) {
                    console.error(`Error parsing data for garden ${gardenId}:`, parseErr);
                    return null;
                }
            });
            return processedGardens.filter((garden): garden is Garden => garden !== null);
        } catch (err) {
            console.error("Error processing gardensData:", err);
            return [];
        }
    }, [raycastingData]);

    useEffect(() => {
        if (!isLoadingRaycast && raycastingData && gardensData.length > 0 && pubData && !pubData.rating) {
            let totalPoints = 0;
            let gardensWithData = 0;
            gardensData.forEach(garden => {
                if (garden?.averageSun?.length > 0) {
                    const gardenPoints = garden.averageSun.reduce((sum, val) => sum + val, 0);
                    totalPoints += gardenPoints;
                    gardensWithData++;
                }
            });

            if (gardensWithData > 0) {
                const averagePointsPerGarden = totalPoints / gardensWithData;
                const rating = calculateBeerMugRating(averagePointsPerGarden);
                setPubData(prev => prev ? { ...prev, rating } : null);
            } else if (gardensData.length > 0) {
                 console.warn("Gardens defined, but no averageSun data found to calculate rating.");
            }
        }
    }, [isLoadingRaycast, raycastingData, gardensData, pubData]);

    const gardenHeatmaps = useMemo(() => {
        if (!raycastingData) return {};
        try {
            const result: Record<string, HeatmapData> = {};
             const validGardenIds = raycastingData.garden_details ? Object.keys(raycastingData.garden_details) : [];
             validGardenIds.forEach(gardenId => {
                 const gardenData = raycastingData[gardenId];
                 if (gardenData?.heatmap_1d?.timestamps && Array.isArray(gardenData.heatmap_1d.values)) {
                     result[gardenId] = gardenData.heatmap_1d;
                 }
             });
            return result;
        } catch (err) {
            console.error("Error processing gardenHeatmaps:", err);
            return {};
        }
    }, [raycastingData]);

    interface ProcessedGarden3D {
        uid: string;
        name: string;
        is_uncertain: boolean;
        ecefCoordinates: Array<[number, number, number]>;
        raycastResults: { [timestamp: string]: number[] };
    }
    const { dynamicMeshUris, dynamicFocalPoint, gardenDataFor3D } = useMemo(() => {
        const defaultReturn = { dynamicMeshUris: [] as string[], dynamicFocalPoint: [0,0,0] as [number, number, number], gardenDataFor3D: [] as ProcessedGarden3D[] };
        if (!raycastingData) return defaultReturn;
        try {
            const googleTilesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            const baseUrl = 'https://tile.googleapis.com';
            const meshUris = raycastingData.mesh_uris || [];
            const sessionId = raycastingData.session_id || '';
            const centreEcef = raycastingData.centre_ecef || null;

            const completeUrls = meshUris.map(uri => {
                const uriPath = uri.startsWith('/') ? uri : `/${uri}`;
                const cleanSessionId = sessionId.startsWith('session=') ? sessionId.substring(8) : sessionId;
                if (!sessionId || !googleTilesApiKey) {
                     console.warn("Missing sessionId or API key for mesh URL generation.");
                     return '';
                 }
                return `${baseUrl}${uriPath}?session=${cleanSessionId}&key=${googleTilesApiKey}`;
            }).filter(url => url !== '');

            let focalPoint = defaultReturn.dynamicFocalPoint;
            if (centreEcef && Array.isArray(centreEcef) && centreEcef.length === 3 && centreEcef.every(n => typeof n === 'number')) {
                focalPoint = centreEcef as [number, number, number];
            } else if (pubData?.latitude && pubData?.longitude) {
                 focalPoint = latLonElevToECEF(pubData.latitude, pubData.longitude, 0);
                 console.warn("Using pub lat/lon + 50m elevation for 3D focal point due to missing/invalid centre_ecef");
            }

            const validGardenIds = raycastingData.garden_details ? Object.keys(raycastingData.garden_details) : [];
            let processedGardenData: ProcessedGarden3D[] = [];
            validGardenIds.forEach(gardenId => {
                 const gardenObject = raycastingData[gardenId];
                 if (!gardenObject) return;
                const coords3D = gardenObject.garden_vertex_coords;
                if (coords3D && Array.isArray(coords3D) && coords3D.length > 0) {
                    const ecefCoords = coords3D.map((coord: number[]) => {
                        if (Array.isArray(coord) && coord.length >= 3 && coord.every(n => typeof n === 'number')) {
                            return latLonElevToECEF(coord[0], coord[1], coord[2]);
                        }
                        return null;
                    }).filter((coord): coord is [number, number, number] => coord !== null);
                    if (ecefCoords.length === 0) return;
                    const hourlyData = gardenObject.hourly_data || {};
                    const gardenName = raycastingData.garden_details?.[gardenId]?.name || `Garden ${gardenId.slice(0, 8)}`;
                    const isUncertain = raycastingData.garden_details?.[gardenId]?.is_uncertain || false;
                    processedGardenData.push({ uid: gardenId, name: gardenName, is_uncertain: isUncertain, ecefCoordinates: ecefCoords, raycastResults: hourlyData });
                }
            });
            return { dynamicMeshUris: completeUrls, dynamicFocalPoint: focalPoint, gardenDataFor3D: processedGardenData };
        } catch (err) {
            console.error("Error processing garden data for 3D view:", err);
            return defaultReturn;
        }
    }, [raycastingData, pubData?.latitude, pubData?.longitude]);

    useEffect(() => {
        if (!pubData || isLoadingPub) return;
        let bounceTracked = false;
        const handleBeforeUnload = () => {
            if (!bounceTracked && pubData && !pubData.garden_drawn && !pubData.has_no_garden && !isEditingGarden) {
                 timeOnPageRef.current = (Date.now() - pageLoadTimeRef.current) / 1000;
                 if (timeOnPageRef.current > 1.5) {
                    trackNoGardenBounce(pubData.google_place_id, timeOnPageRef.current);
                    bounceTracked = true;
                 }
             }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
             if (!bounceTracked && pubData && !pubData.garden_drawn && !pubData.has_no_garden && !isEditingGarden) {
                 timeOnPageRef.current = (Date.now() - pageLoadTimeRef.current) / 1000;
                 if (timeOnPageRef.current > 1.5) {
                     trackNoGardenBounce(pubData.google_place_id, timeOnPageRef.current);
                 }
             }
        };
    }, [pubData, isLoadingPub, isEditingGarden]);

    useEffect(() => {
        if (pubData && !isLoadingRaycast && gardensData.length > 0) {
             const startTime = Date.now();
             return () => {
                 if (pubData) {
                    trackGardenViewDuration(pubData.google_place_id, gardensData.length, (Date.now() - startTime) / 1000);
                 }
             };
        }
         return () => {};
    }, [pubData, isLoadingRaycast, gardensData]);

     useEffect(() => {
         if (dynamicMeshUris.length > 0 && sceneLoadStartTime && pubData) {
             const timer = setTimeout(() => {
                 const loadTime = (Date.now() - sceneLoadStartTime) / 1000;
                 track3DSceneLoadingTime(loadTime, pubData.google_place_id, dynamicMeshUris.length);
             }, 4000);
             return () => clearTimeout(timer);
         }
          return () => {};
     }, [dynamicMeshUris, sceneLoadStartTime, pubData]);

     useEffect(() => {
         if (dynamicMeshUris.length > 0 && !sceneLoadStartTime) {
             setSceneLoadStartTime(Date.now());
         } else if (dynamicMeshUris.length === 0 && sceneLoadStartTime) {
             setSceneLoadStartTime(null);
         }
     }, [dynamicMeshUris, sceneLoadStartTime]);

    const existingGardensForEditor = useMemo(() => {
        if (!raycastingData?.garden_details || !raycastingData?.garden_polygons) {
            return [];
        }

        return Object.keys(raycastingData.garden_details)
            .reduce<ExistingGarden[]>((acc, uid) => {
                const details = raycastingData.garden_details?.[uid];
                const polyData = raycastingData.garden_polygons?.[uid];

                if (!details || !polyData) {
                    return acc;
                }

                acc.push({
                    uid,
                    name: details.name,
                    polygon: polyData.polygon,
                    elevation: polyData.elevation ?? 0,
                    is_uncertain: details.is_uncertain,
                    is_owner: details.is_owner || false,
                });
                return acc;
            }, []);
    }, [raycastingData]);

    // Add timeout handler to prevent infinite loading and server errors
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (isLoadingPub) {
                console.warn("Pub loading timeout reached");
                setError("Unable to load pub data - request timed out");
                setIsLoadingPub(false);
            }
        }, 10000); // 10 second timeout
        return () => clearTimeout(timeout);
    }, [isLoadingPub]);

    useEffect(() => {
        // Always set canonical URL to /pub/:id format if we have a pub ID
        const canonicalLink = document.querySelector('link[rel="canonical"]');
        if (canonicalLink && effectiveGooglePlaceId) {
            canonicalLink.setAttribute('href', `https://sun-tap.com/pub/${effectiveGooglePlaceId}`);
        }

        if (pubData && !isLoadingPub) {
            let cityToUse = pubData.city;
            if (!cityToUse && pubData.address) {
                cityToUse = extractCity(pubData.address) || undefined;
            }
            const effectiveCity = cityToUse || 'London';
            document.title = `${pubData.name} - Sunny Pub Garden in ${effectiveCity} | Suntap`;
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) metaDescription.setAttribute('content', `Find the sunniest spots at ${pubData.name} in ${effectiveCity}. Real-time sun tracking shows exactly where and when this pub garden will be in sunshine.`);
            
            const scriptTag = document.getElementById('pub-json-ld');
            if (scriptTag) {
                try {
                    scriptTag.textContent = JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "LocalBusiness",
                        "name": pubData.name,
                        "address": { "@type": "PostalAddress", "addressLocality": effectiveCity, "streetAddress": pubData.address || "" },
                        "geo": { "@type": "GeoCoordinates", "latitude": pubData.latitude, "longitude": pubData.longitude },
                        "url": `https://sun-tap.com/pub/${pubData.google_place_id}`,
                        "image": "https://sun-tap.com/web-app-manifest-512x512.png",
                        "description": `Find the sunniest spots at ${pubData.name} in ${effectiveCity}. Our 3D sun tracker shows exactly which parts of this garden are in sunshine throughout the day.`,
                        "areaServed": effectiveCity,
                        "servesCuisine": "Pub Food",
                        ...(pubData.rating && pubData.rating > 0 && {
                           "aggregateRating": {
                             "@type": "AggregateRating",
                             "ratingValue": pubData.rating.toFixed(1),
                             "bestRating": "5",
                             "worstRating": "1",
                             "ratingCount": 1
                           }
                        })
                    });
                } catch (e) {
                    console.error("Failed to stringify JSON-LD", e);
                }
            }
        } else if (!isLoadingPub) {
            // Fallback title and description when no pub data
            if (!effectiveGooglePlaceId) {
                document.title = 'Suntap - Find Sunny Pub Gardens';
                const metaDescription = document.querySelector('meta[name="description"]');
                if (metaDescription) metaDescription.setAttribute('content', 'Discover the best sunny pub gardens near you. Suntap uses 3D mapping to show you exactly where the sun will be.');
                if (canonicalLink) canonicalLink.setAttribute('href', `https://sun-tap.com${location.pathname}`);
            } else {
                // We have pub ID but no data yet - set appropriate fallback title
                document.title = 'Pub Sun Analysis | Suntap';
                const metaDescription = document.querySelector('meta[name="description"]');
                if (metaDescription) metaDescription.setAttribute('content', 'Real-time 3D sun tracking for pub gardens. Find the sunniest spots and best times to visit.');
            }
            const scriptTag = document.getElementById('pub-json-ld');
            if (scriptTag) scriptTag.textContent = '{}';
        }
    }, [pubData, isLoadingPub, location.pathname, effectiveGooglePlaceId]);

    const showViewSunlightExperience = !isLoadingPub && pubData?.garden_drawn === true && pubData?.has_no_garden !== true;
    const showSubmitGardenExperience = !isLoadingPub && pubData && pubData.garden_drawn === false;
    const isShowingGardenEditor = (isEditingGarden || showSubmitGardenExperience) && pubData?.google_place_id;
    
    const showGardenContent = showViewSunlightExperience && !isLoadingRaycast && raycastingData && gardensData.length > 0 && !error;
    // Always show loading indicator when fetching raycasting data, regardless of other conditions
    const showLoadingIndicator = isLoadingRaycast || (showViewSunlightExperience && (!raycastingData && !error));
    const showError = showViewSunlightExperience && !!error;
    const showProcessingError = showViewSunlightExperience && !isLoadingRaycast && raycastingData && gardensData.length === 0 && !error;
    
    const shouldRenderSheetContent = !isLoadingPub && pubData && !error;

    const handleSubmitGarden = () => {
        if (pubData) {
            trackGardenEditStarted(pubData.google_place_id, gardensData.length);
            setIsEditingGarden(true);
        }
    };

    const handleOpenAttribution = useCallback((copyrights?: string[]) => {
        if (copyrights) {
            setTileCopyrights(copyrights);
        }
        setAttributionOpen(true);
    }, []);

    const handleOpenShareModal = () => {
        if (pubData) {
            trackShareInitiated(pubData.google_place_id, pubData.name, !!(pubData.garden_drawn && !pubData.has_no_garden));
        }
        setShareModalOpen(true);
    };

    const handleReturnToPrevious = () => {
        if (pubData) {
            timeOnPageRef.current = (Date.now() - pageLoadTimeRef.current) / 1000;
            trackReturnToSearch(pubData.google_place_id, !!(pubData.garden_drawn && !pubData.has_no_garden), timeOnPageRef.current);
        }
        if (window.history.length > 2) {
             navigate(-1);
        } else {
            navigate('/');
        }
    };

    const getGardenSizeText = () => {
        if (!raycastingData?.garden_details) return null;
        
        // Get the total garden size from all gardens
        const gardenSizes = Object.values(raycastingData.garden_details)
            .map(garden => garden.garden_size)
            .filter((size): size is number => typeof size === 'number' && size > 0);
        
        if (gardenSizes.length === 0) return null;
        
        // Sum all garden sizes
        const totalSize = gardenSizes.reduce((sum, size) => sum + size, 0);
        
        const sizeCategory = categorizeGardenSize(totalSize);
        return sizeCategory.charAt(0).toUpperCase() + sizeCategory.slice(1) + ' garden';
    };

    const getCurrentUrl = () => window.location.href;

    const handleBackButtonClick = () => {
        if (window.history.length > 1) {
            navigate(-1);
            if (pubData) {
                timeOnPageRef.current = (Date.now() - pageLoadTimeRef.current) / 1000;
                trackReturnToSearch(pubData.google_place_id, !!(pubData.garden_drawn && !pubData.has_no_garden), timeOnPageRef.current);
            }
        } else {
            navigate('/');
        }
    };

    const renderShadedVertexViewer = () => {
         if (!showGardenContent) return null;
        return (
             <Suspense fallback={
                 <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4, background: 'rgba(0,0,0,0.1)' }}>
                     <CircularProgress size={60} sx={{ color: themeColors.blue, mb: 2 }} />
                     <Typography variant="h6" sx={{ fontFamily: "ApfelGrotezkRegular", color: themeColors.lightBlue }}>Loading 3D map...</Typography>
                 </Box>
             }>
                {(dynamicMeshUris.length > 0) ? (
                     <ShadedVertexViewer
                         meshUris={dynamicMeshUris}
                         initialZoom={40}
                         focalPoint={dynamicFocalPoint}
                         gardenData={gardenDataFor3D}
                         sunVectors={raycastingData?.sun_vector}
                         height="100%"
                         onOpenAttribution={handleOpenAttribution}
                         bottom={isMobile ? `${PEEK_HEIGHT_NUM + 5}px` : '10px'}
                         marginLeft={isMobile ? 'auto' : '440px'}
                         gardenPolygons={raycastingData?.garden_polygons}
                     />
                 ) : (
                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 3, background: 'rgba(0,0,0,0.1)' }}>
                         <Typography variant="h6" color="textSecondary" fontFamily="ApfelGrotezkRegular">
                             3D View Unavailable
                         </Typography>
                         <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 400 }}>
                              The 3D map data for this location could not be loaded. Sunlight timeline data below may still be available.
                         </Typography>
                     </Box>
                 ) }
             </Suspense>
        );
    };

    const renderCanvasOverlayContent = () => {
        const commonOverlayStyles = {
            position: 'absolute', top: 0, left: 0, width: '100%',
            height: '100%',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', p: 3,
            background: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(4px)',
            zIndex: 2,
            pointerEvents: 'none',
            boxSizing: 'border-box',
        };
         const interactiveOverlayStyles = { ...commonOverlayStyles, pointerEvents: 'auto' };

        if (isLoadingPub) {
             return (
                 <Box sx={commonOverlayStyles}>
                     <CircularProgress size={60} sx={{ mb: 2, color: themeColors.blue }} />
                     <Typography variant="h6" fontFamily="ApfelGrotezkRegular" color="text.primary">
                         Loading pub details...
                     </Typography>
                 </Box>
             );
         }
        // Always show loading spinner when fetching raycasting data
        if (isLoadingRaycast) {
            return (
                <Box sx={commonOverlayStyles}>
                    <CircularProgress size={60} sx={{ mb: 2, color: themeColors.blue }} />
                    <Typography variant="h6" fontFamily="ApfelGrotezkRegular" color="text.primary">
                        Loading sun data...
                    </Typography>
                </Box>
            );
        }
        if (showLoadingIndicator) {
            return (
                <Box sx={commonOverlayStyles}>
                    <CircularProgress size={60} sx={{ mb: 2, color: themeColors.blue }} />
                    <Typography variant="h6" fontFamily="ApfelGrotezkRegular" color="text.primary">
                        Loading sun data...
                    </Typography>
                </Box>
            );
        }
        if (showError) {
             return (
                 <Box sx={interactiveOverlayStyles}>
                    <Typography variant="h5" color="error" gutterBottom sx={{ fontFamily: "ApfelGrotezkRegular" }}>
                        Data Error
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: 600, mb: 3 }}>
                        {error}
                    </Typography>
                     <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                         <Button variant="contained" onClick={handleReturnToPrevious}>Back to Search</Button>
                     </Box>
                 </Box>
             );
        }
         if (showProcessingError) {
              return (
                  <Box sx={interactiveOverlayStyles}>
                     <Typography variant="h6" color="textSecondary" fontFamily="ApfelGrotezkRegular">
                         Garden Data Issue
                     </Typography>
                     <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 3, maxWidth: 600 }}>
                          We couldn't process the garden data for {pubData?.name}. This might be a temporary issue, or the submitted garden might need correction.
                     </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                          <SubmitGardenButton startIcon={<EditIcon />} onClick={handleSubmitGarden}>
                              Edit Garden for {pubData.name}
                          </SubmitGardenButton>
                         <Button variant="contained" onClick={handleReturnToPrevious}>Back to Search</Button>
                      </Box>
                  </Box>
              );
         }
        return null;
    };

    const ActionButtons = () => {
        return <Stack
            direction="row"
            sx={{
                position: 'absolute',
                bottom: 0,
                width: '100%',
                px: '10px',
                py: '10px',
                zIndex: 100,
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <Button
                sx={HeaderButtonStyle}
                onClick={(e) => {
                    e.stopPropagation();
                    if (pubData?.latitude && pubData?.longitude) {
                        const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pubData.name)}&query_place_id=${pubData.google_place_id}`;
                        window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
                    }
                }}
                disabled={!pubData?.latitude || !pubData?.longitude}
                endIcon={<LocationOnIcon />}
            >
                Directions
            </Button>
            <Button
                sx={HeaderButtonStyle}
                onClick={handleOpenShareModal}
                disabled={!pubData}
                endIcon={<ShareIcon />}
            >
                Share
            </Button>
            {isEditingGarden ? (
                <Button
                    sx={{ ...HeaderButtonStyle, borderColor: themeColors.orange, color: themeColors.orange }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingGarden(false);
                    }}
                >
                    Cancel
                </Button>
            ) : (
                <Button
                    sx={HeaderButtonStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSubmitGarden();
                    }}
                    disabled={!pubData || isLoadingPub || (showViewSunlightExperience && isLoadingRaycast)}
                    endIcon={showSubmitGardenExperience ? <AddIcon /> : <EditIcon />}
                >
                    {showSubmitGardenExperience ? "Map Garden" : "Edit Garden"}
                </Button>
            )}
        </Stack>
    }

    const SharedContent = () => (
        <>
            {showGardenContent && raycastingData?.garden_details && (
                <Box sx={{ mb: 3 }}>
                    <Box>
                        {Object.entries(raycastingData.garden_details).map(([gardenId, gardenInfo]) => (
                            <Box key={gardenId}>
                                <Paper variant="outlined" sx={{ borderRadius: '12px', height: '100%', background: 'transparent', p: 2, boxShadow: 'none', border: 'none' }}>
                                                            <Typography variant="h6" sx={{ mb: 1, color: 'black', fontWeight: 600, display: 'flex', alignItems: 'center' }} >
                            {gardenInfo.name || `Garden ${gardenId.slice(0, 8)}`}
                            {gardenInfo.is_uncertain &&
                                <HelpOutlineIcon titleAccess="Garden location is uncertain" sx={{ ml: 1, fontSize: '1.1rem', color: themeColors.orange, verticalAlign: 'middle' }} />
                            }
                            {raycastingData?.coverage_quality && raycastingData.coverage_quality !== 'HIGH' && (
                                <Tooltip title="These results are outside of our high detail coverage area, so may not be as precise as predictions made in higher population areas.">
                                    <WarningIcon sx={{ ml: 1, fontSize: '1.1rem', color: '#FFA726', cursor: 'pointer' }} />
                                </Tooltip>
                            )}
                        </Typography>
                                    {gardenInfo.is_uncertain &&
                                        <Typography variant="caption" display="block" sx={{ mb: 1, color: themeColors.orange, fontStyle: 'italic' }} >
                                            Sun data accuracy may be affected.
                                        </Typography>
                                    }
                                    <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
                                        Today's sunshine:
                                    </Typography>
                                    {gardenHeatmaps[gardenId] ? (
                                        <Box sx={{ background: 'transparent', backdropFilter: 'none', height: isMobile ? '100px' : '120px', width: '100%', padding: '5px', border: 'none', borderRadius: '0' }}>
                                            <IntensityTimeline
                                                data={convertToIntensityData(gardenHeatmaps[gardenId], gardenId)}
                                                googlePlaceId={gardenId}
                                                sunriseTime={raycastingData?.sun_times?.sunrise}
                                                sunsetTime={raycastingData?.sun_times?.sunset}
                                                duskDawnDurationMinutes={30}
                                                mode={timelineMode}
                                                isBot={isBot}
                                                pubName={pubData?.name || 'this pub'}
                                                gardenName={gardenInfo.name || `Garden ${gardenId.slice(0, 8)}`}
                                            />
                                        </Box>
                                    ) : (
                                        <Box sx={{ background: 'transparent', height: isMobile ? '100px' : '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: muiTheme.palette.text.disabled, fontSize: '0.8rem' }}>
                                            No sunlight data
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {pubData?.address && (
                <Typography variant="h6" color="text.primary" display="block" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', mt: 0.25, mb: 3, pl: '16px' }} >
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pubData.address)}${pubData.google_place_id ? `&query_place_id=${pubData.google_place_id}` : ''}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }} >
                        [{pubData.address}]
                    </a>
                </Typography>
            )}

            {shouldRenderSheetContent && pubData && (
                <Box sx={{ mb: 3 }}>
                    <Stack direction={'row'}>
                        <Typography variant="h5" sx={{ mb: 2, mr: 1, fontWeight: 600, fontFamily: "ApfelGrotezkRegular", color: themeColors.darkBackground }} >
                            Nearby Pubs
                        </Typography>
                        <MouseIcon />
                    </Stack>
                    {pubData.latitude && pubData.longitude ? (
                        <NearbyPubs currentPubId={pubData.google_place_id} preloadedPubs={nearbyPubsData} isLoading={isLoadingNearby} />
                    ) : (
                        <Box sx={{ py: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Cannot load nearby pubs (missing coordinates)
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {showSubmitGardenExperience && (
                 <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                     <Button variant="text" onClick={handleReturnToPrevious} sx={{ color: 'text.secondary' }}>
                         Back to Search
                     </Button>
                 </Box>
            )}
        </>
    );

    const MobileSheetContent = () => ( <> <SharedContent /> <Box sx={{ mt: 4, mb: 4.5 }}> <Footer /> </Box> </> );
    const SideMenuContent = () => ( <> <SharedContent /> <Box sx={{ mt: 4, mb: 4.5 }}> <Footer /> </Box> </> );

    // Memoize mesh data for SubmitGarden
    const reusableMeshData = useMemo<FullMeshResponse | null>(() => {
        if (raycastingData?.mesh_uris && raycastingData?.session_id) {
            return {
                mesh_uris: raycastingData.mesh_uris,
                centre_ecef: raycastingData.centre_ecef,
                session_id: raycastingData.session_id,
                copyrights: raycastingData.copyrights,
                has_no_garden: raycastingData.has_no_garden,
            };
        }
        return null;
    }, [raycastingData]);

    const isPubModerated = raycastingData?.garden_moderated ?? pubData?.garden_moderated ?? false;

    const renderMainContent = () => {
        // Bot detection: render simple content for crawlers
        if (isBot) {
            // Always provide content for bots to prevent soft 404s
            if (!pubData && !isLoadingPub) {
                return (
                    <Box sx={{ p: 3, textAlign: 'left', height: '100%', background: 'white' }}>
                        <Typography variant="h1" component="h1" sx={{ mb: 2, fontFamily: "ApfelGrotezkRegular" }}>
                            Pub Information
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, mb: 2, lineHeight: 1.6 }}>
                            This pub's information is being updated. Please check back soon.
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, mb: 2, lineHeight: 1.6 }}>
                            Suntap provides real-time 3D sun tracking for pub gardens across the UK. 
                            Our technology shows you exactly which parts of a pub garden will be sunny throughout the day, 
                            helping you find the perfect spot for outdoor dining and drinks.
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                            Features include:
                        </Typography>
                        <ul style={{ marginLeft: '20px', lineHeight: 1.6 }}>
                            <li>Interactive 3D sun tracking visualization</li>
                            <li>Hour-by-hour sunlight predictions</li>
                            <li>Garden mapping and sun analysis</li>
                            <li>Best sunny spots recommendations</li>
                            <li>Real-time weather integration</li>
                        </ul>
                        <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
                            Visit our homepage to search for sunny pub gardens in your area.
                        </Typography>
                    </Box>
                );
            }
            
            return (
                <Box sx={{ p: 3, textAlign: 'left', height: '100%', background: 'white' }}>
                    <Typography variant="h4" component="h1" sx={{ mb: 2, fontFamily: "ApfelGrotezkRegular" }}>
                        {pubData?.name || 'Pub Sun Analysis'}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2, mb: 2, lineHeight: 1.6 }}>
                        This page contains a real-time 3D visualization of the sun's path over the garden at {pubData?.name || 'this pub'}
                        {pubData?.address && `, located at ${pubData.address}`}. 
                        Our technology calculates which tables will be in the sun throughout the day.
                    </Typography>
                    {pubData?.rating && pubData.rating > 0 && (
                        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                            Sun Rating: {pubData.rating.toFixed(1)} out of 5 suns
                        </Typography>
                    )}
                    <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                        Features include:
                    </Typography>
                    <ul style={{ marginLeft: '20px', lineHeight: 1.6 }}>
                        <li>Interactive 3D sun tracking visualization</li>
                        <li>Hour-by-hour sunlight predictions</li>
                        <li>Garden mapping and sun analysis</li>
                        <li>Best sunny spots recommendations</li>
                    </ul>
                    {pubData?.address && (
                        <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
                            Address: {pubData.address}
                        </Typography>
                    )}
                </Box>
            );
        }

        if (isLoadingPub || isProcessingSubmission) { // isProcessingSubmission is kept for other potential uses
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#e0e0e0' }}>
                    <CircularProgress />
                    {isProcessingSubmission && (
                        <Typography sx={{ ml: 2 }}>Processing...</Typography>
                    )}
                </Box>
            );
        } else if (isShowingGardenEditor) {
            // We now pass an 'isEditing' flag to differentiate between "Edit Garden" mode
            // and the "Map Garden" mode for a new pub.
            const isEditMode = isEditingGarden && gardensData.length > 0;
            return (
                <Suspense fallback={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                }>
                    <SubmitGardenMapbox 
                        googlePlaceId={pubData!.google_place_id} 
                        onExitAndRefresh={handleExitAndRefresh}
                        onCancel={handleCancelEditing}
                        isModerated={isPubModerated}
                        isEditing={isEditMode}
                    />
                </Suspense>
            );
        } else if (showViewSunlightExperience) {
            return (
                <>
                    {renderShadedVertexViewer()}
                    {renderCanvasOverlayContent()}
                </>
            );
        } else {
             // This case handles the initial "No garden mapped yet" overlay, which is now inside the editor.
             // So we show a generic loader or empty state if needed.
             return renderCanvasOverlayContent();
        }
    }

    return (
        <>
            <Helmet>
                <title>{isLoadingPub ? 'Loading pub details...' : `${pubData?.name || 'Pub'} | Sun Analysis | Suntap`}</title>
                
            </Helmet>
            <Fonts />
            <Box sx={{ height: '100vh', width: '100vw', position: 'relative', overflow: 'hidden', background: muiTheme.palette.background.default, boxSizing: 'border-box', display: 'flex', }}>
                <IconButton onClick={handleBackButtonClick} sx={{ position: 'fixed', top: 16, left: 16, zIndex: 6, backgroundColor: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)', }, width: 40, height: 40, }} aria-label="Back" >
                    <ArrowBackIcon sx={{ color: 'rgba(0,0,0,0.6)' }} />
                </IconButton>

                {!isMobile && !isShowingGardenEditor && (
                    <Box sx={{ width: 400, height: '100%', position: 'relative', zIndex: 4, background: 'linear-gradient(to bottom, #FBE858 0%, #F8DAEE 100%)', borderRadius: '0 16px 16px 0', display: 'flex', flexDirection: 'column', overflow: 'hidden', }} >
                        {shouldRenderSheetContent && pubData && ( <ActionButtons /> )}
                        <Box sx={{ p: 3, pt: 4, flexShrink: 0, }} >
                            <Box sx={{ mt: 4, mb: 1 }}>
                                {isPubModerated && <VerifiedBadge sx={{ mb: 1 }} />}
                                <Typography variant="h4" sx={{ fontFamily: "ApfelGrotezkRegular", fontWeight: 700, color: themeColors.darkBackground, textAlign: 'left', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', }} >
                                    {pubData?.name || (isLoadingPub ? 'Loading...' : 'Pub Details')}
                                </Typography>
                            </Box>
                            {shouldRenderSheetContent && pubData && (
                                <Box sx={{ width: '100%', textAlign: 'left' }}>
                                    {hasUserLocation && userDistance !== undefined && (
                                        <Typography variant="body2" color="text.primary" display="block">
                                            Distance: {userDistance >= 1000 ? `${(userDistance / 1000).toFixed(1)} km` : `${Math.round(userDistance)} m`}
                                            {getGardenSizeText() && ` • ${getGardenSizeText()}`}
                                        </Typography>
                                    )}
                                    {pubData.rating !== undefined && pubData.rating > 0 && (
                                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}> Sun Rating: </Typography>
                                            <Rating name={`rating-${pubData.google_place_id}-header`} value={pubData.rating} readOnly precision={0.5} size="small" icon={<SunIcon fontSize="inherit" sx={{ color: '#FFB400' }} />} emptyIcon={<SunIcon fontSize="inherit" sx={{ color: 'rgba(0, 0, 0, 0.26)' }} />} sx={{ '& .MuiRating-icon': { marginRight: '1px' } }} />
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 3, py: 2, }} >
                            {shouldRenderSheetContent && pubData && ( <SideMenuContent /> )}
                        </Box>
                    </Box>
                )}

                <Box
                    sx={{
                        position: 'relative',
                        flex: isMobile ? 'none' : 1,
                        width: isMobile ? '100%' : 'auto',
                        height: isMobile ? (isShowingGardenEditor ? '100%' : `calc(100% - ${PEEK_HEIGHT_NUM}px + 16px)`) : '100%',
                        zIndex: 1,
                        background: isMobile ? 'transparent' : '#e0e0e0',
                    }}
                >
                    {renderMainContent()}
                </Box>

                {isMobile && !isShowingGardenEditor && (
                    <BottomSheet
                        open={true}
                        onDismiss={() => { setIsSheetExpanded(false); sheetRef.current?.snapTo(({ snapPoints }) => snapPoints[0]); }}
                        ref={sheetRef}
                        snapPoints={({ minHeight, maxHeight }) => [ PEEK_HEIGHT_NUM, Math.min(maxHeight, window.innerHeight * (MAX_SHEET_HEIGHT_VH / 100)) ]}
                        defaultSnap={({ snapPoints }) => isSheetExpanded ? snapPoints[1] : snapPoints[0] }
                        onSpringStart={(event) => { if (event.type === 'SNAP' && event.source === 'user') { setIsSheetExpanded(prev => !prev); } }}
                        blocking={false}
                        initialFocusRef={false}
                        skipInitialTransition={true}
                        expandOnContentDrag={false}
                        header={
                            <>
                                <Box
                                    onClick={() => setIsSheetExpanded(prev => !prev)}
                                    sx={{ height: isSheetExpanded ? `${PEEK_HEIGHT_EXPANDED}px` : `${PEEK_HEIGHT_NUM}px`, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', position: 'relative', cursor: 'pointer', flexShrink: 0, px: { xs: 2, sm: 3 }, pt: '16px', boxSizing: 'border-box', width: '100%', }}
                                >
                                    {isPubModerated && <VerifiedBadge sx={{ mb: 0.5 }} />}
                                    <Typography variant="h3" noWrap sx={{ fontFamily: "ApfelGrotezkRegular", fontWeight: 700, color: themeColors.darkBackground, textAlign: 'left', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none', }} >
                                        {pubData?.name || (isLoadingPub ? 'Loading...' : 'Pub Details')}
                                    </Typography>
                                                                            {shouldRenderSheetContent && pubData && (
                                            <Box sx={{ width: '100%', mt: '10px', textAlign: 'left' }}>
                                                {hasUserLocation && userDistance !== undefined && (
                                                    <Typography variant="body2" color="text.primary" display="block">
                                                        Distance: {userDistance >= 1000 ? `${(userDistance / 1000).toFixed(1)} km` : `${Math.round(userDistance)} m`}
                                                        {getGardenSizeText() && ` • ${getGardenSizeText()}`}
                                                    </Typography>
                                                )}
                                            {pubData.rating !== undefined && pubData.rating > 0 && (
                                                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}> Sun Rating: </Typography>
                                                    <Rating name={`rating-${pubData.google_place_id}-header`} value={pubData.rating} readOnly precision={0.5} size="small" icon={<SunIcon fontSize="inherit" sx={{ color: '#FFB400' }} />} emptyIcon={<SunIcon fontSize="inherit" sx={{ color: 'rgba(0, 0, 0, 0.26)' }} />} sx={{ '& .MuiRating-icon': { marginRight: '1px' } }} />
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </>
                        }
                        style={{
                            maxWidth: MAX_SHEET_WIDTH,
                            margin: '0 auto',
                            px: ' 0 !important',
                            zIndex: 4,
                            '--rsbs-bg': isSheetExpanded
                                ? 'linear-gradient(to bottom, #FBE858 0%, #F8DAEE 100%)'
                                : '#FBE858',
                            '--rsbs-handle-bg': 'white',
                            '--rsbs-backdrop-bg': 'rgba(0, 0, 0, 0.3)',
                            '--rsbs-border-radius': SHEET_BORDER_RADIUS,
                            '--rsbs-max-w': MAX_SHEET_WIDTH,
                            '--rsbs-ml': 'auto',
                            '--rsbs-mr': 'auto',
                            '--rsbs-content-max-height': 'none',
                            '--rsbs-transition-duration': '0.35s',
                            '--rsbs-transition-timing-function': 'cubic-bezier(0.4, 0, 0.2, 1)',
                            '--rsbs-overlay-translate-y': '0px !important',
                            '--rsbs-overlay-rounded': SHEET_BORDER_RADIUS,
                            '--rsbs-min-h': `${PEEK_HEIGHT_NUM}px`,
                        } as any}
                    >
                        {<ActionButtons />}
                        {shouldRenderSheetContent && pubData && (
                            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1, pb: 4, minHeight: `calc(${MAX_SHEET_HEIGHT_VH}vh - ${PEEK_HEIGHT_NUM}px - 50px)`, }} >
                                <MobileSheetContent />
                            </Box>
                        )}
                    </BottomSheet>
                )}

                <Dialog open={attributionOpen} onClose={() => setAttributionOpen(false)} PaperProps={{ sx: { borderRadius: '12px', maxWidth: '90%', maxHeight: '80%' } }} >
                    <DialogTitle id="attribution-dialog-title" sx={{ fontFamily: "ApfelGrotezkRegular" }}> Data Sources </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}> 3D data is provided by: </Typography>
                        <List dense sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                            {isLoadingRaycast && tileCopyrights.length === 0 && (
                                <ListItem>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CircularProgress size={20} sx={{ mr: 2, color: themeColors.blue }} />
                                        <ListItemText primary="Loading attribution..." primaryTypographyProps={{ variant: 'body2', style: { fontSize: '0.9rem' } }} />
                                    </Box>
                                </ListItem>
                            )}
                            {tileCopyrights.length > 0 && tileCopyrights.map((copyright: string, index: number) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText primary={copyright} primaryTypographyProps={{ variant: 'body2', style: { fontSize: '0.9rem' } }} />
                                </ListItem>
                            ))}
                            {!isLoadingRaycast && raycastingData?.copyrights && raycastingData.copyrights.map((copyright: string, index: number) => (
                                <ListItem key={`legacy-${index}`} sx={{ py: 0.5 }}>
                                    <ListItemText primary={copyright} primaryTypographyProps={{ variant: 'body2', style: { fontSize: '0.9rem' } }} />
                                </ListItem>
                            ))}
                            {!isLoadingRaycast && tileCopyrights.length === 0 && (!raycastingData?.copyrights || raycastingData.copyrights.length === 0) && (
                                <ListItem>
                                    <ListItemText primary="Attribution information not available" primaryTypographyProps={{ variant: 'body2', style: { fontStyle: 'italic' } }} />
                                </ListItem>
                            )}
                        </List>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAttributionOpen(false)} variant="contained" size="small"> Close </Button>
                    </DialogActions>
                </Dialog>

                {pubData && ( <ShareModal open={shareModalOpen} onClose={() => setShareModalOpen(false)} pubName={pubData.name} pubAddress={pubData.address} currentUrl={getCurrentUrl()} /> )}

                <script id="pub-json-ld" type="application/ld+json"></script>
            </Box>
        </>
    );
};

export default ViewPub;